<script>
import axios from 'axios';

export default {
  name: 'FulfillmentSubStatusSelect',

  props: {
    modelValue: { type: [String, Number], default: null },
    // Path after VUE_APP_API_BASEURL
    endpoint: { type: String, default: 'fulfillment-sub-statuses' },
    // Optional parent status id; the list reloads when it changes
    statusId: { type: [String, Number], default: null },
    valueKey: { type: String, default: 'id' },
    labelKey: { type: String, default: 'name' },
    // Name of the current sub status from the API (e.g. "Pending Shipment").
    // Used to pre-select the matching option when no id is available.
    selectedLabel: { type: String, default: null },
    label: { type: String, default: 'Fulfillment Status Remarks' },
    placeholder: { type: String, default: 'Select a remark' },
    disabled: { type: Boolean, default: false },
  },

  emits: ['update:modelValue', 'change'],

  data() {
    return {
      selectId: 'sub-status-' + Math.random().toString(36).slice(2, 8),
      options: [],
      loading: false,
      error: '',
      controller: null,
    };
  },

  watch: {
    statusId() {
      this.fetchSubStatuses();
    },
    selectedLabel() {
      this.applySelectedLabel();
    },
  },

  mounted() {
    this.fetchSubStatuses();
  },

  beforeUnmount() {
    if (this.controller) this.controller.abort();
  },

  methods: {
    fetchSubStatuses() {
      // Cancel a request still in flight (e.g. statusId changed quickly)
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      this.loading = true;
      this.error = '';

      const params = {};
      if (this.statusId !== null && this.statusId !== '') {
        params.status_id = this.statusId;
      }

      axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
      axios.get(process.env.VUE_APP_API_BASEURL + this.endpoint, {
        params,
        signal: this.controller.signal,
      })
        .then(response => {
          const data = response.data;
          // Supports a plain array or a { data: [...] } wrapper
          this.options = Array.isArray(data) ? data : (data.data || []);

          // Pre-select from the current sub status name, if given
          if (this.applySelectedLabel()) return;

          // Clear the selection if it's no longer in the list
          const stillValid = this.options.some(
            o => String(o[this.valueKey]) === String(this.modelValue)
          );
          if (this.modelValue !== null && !stillValid) {
            this.$emit('update:modelValue', null);
          }
        })
        .catch(error => {
          if (axios.isCancel(error)) return;
          this.options = [];
          this.error = 'Could not load remarks.';
          console.error('Error fetching fulfillment sub statuses:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Selects the option whose label matches selectedLabel.
    // Returns true if a match was found.
    applySelectedLabel() {
      if (!this.selectedLabel || !this.options.length) return false;

      const target = String(this.selectedLabel).trim().toLowerCase();
      const match = this.options.find(
        o => String(o[this.labelKey]).trim().toLowerCase() === target
      );
      if (!match) return false;

      if (String(match[this.valueKey]) !== String(this.modelValue)) {
        this.$emit('update:modelValue', match[this.valueKey]);
      }
      return true;
    },

    onChange(event) {
      const selected = this.options.find(
        o => String(o[this.valueKey]) === event.target.value
      );
      this.$emit('update:modelValue', selected ? selected[this.valueKey] : null);
      this.$emit('change', selected || null);
    },
  },
};
</script>

<template>
  <div class="col-md-4">
    <label
      :for="selectId"
      class="text-muted text-uppercase fw-semibold d-block mb-1"
    >
      {{ label }}
    </label>

    <span class="fw-bold text-primary ">
      <select
        :id="selectId"
        class="form-select"
        :value="modelValue ?? ''"
        :disabled="disabled || loading || !!error"
        @change="onChange"
      >
        <option value="" disabled>
          {{ loading ? 'Loading…' : placeholder }}
        </option>
        <option
          v-for="item in options"
          :key="item[valueKey]"
          :value="item[valueKey]"
        >
          {{ item[labelKey] }}
        </option>
      </select>
    </span>

    <div v-if="error" class="text-danger mt-1">
      {{ error }}
      <a href="#" class="ms-1" @click.prevent="fetchSubStatuses">Retry</a>
    </div>
  </div>
</template>